import { Link } from 'react-router-dom';
import {
  GraduationCap, BookOpen, Exam, FileText, ArrowRight, CheckCircle, Star,
  ShieldCheck, Handshake, Sparkle, MagnifyingGlass, SealCheck,
} from '@phosphor-icons/react';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/88c4f22e-4842-4985-9860-1a61b44116dd/images/4e2b7cc1e7607dc51a3ad461b1d10247ea1646b74900b25b0f87364ad06ba245.png';

// Scarcity-first stat set. Numbers deliberately understated to reinforce
// exclusivity — quality over quantity.
const stats = [
  { value: '4', label: 'Partner Colleges', hint: 'Hand-picked in Canada' },
  { value: '1 in 12', label: 'Recruiter Acceptance', hint: 'Applications we approve' },
  { value: '48 hr', label: 'Vetting Time', hint: 'Before we say yes' },
  { value: '100%', label: 'Programs Verified', hint: 'PTIB / regulated' },
];

const services = [
  { icon: GraduationCap, title: 'College Admissions', desc: 'Expert guidance to secure admission at our curated network of Canadian colleges.' },
  { icon: BookOpen, title: 'Career Pathway Guidance', desc: 'Personalized planning built around each student\u2019s ambitions and eligibility.' },  { icon: Exam, title: 'Exam Preparation', desc: 'Focused prep for Accuplacer, CLBPT, iTEP, IELTS and other required tests.' },
  { icon: FileText, title: 'Applications & Documents', desc: 'End-to-end handling so paperwork never becomes the reason a student is delayed.' },
];

// The 4-step vetting funnel — the story of "why we're small on purpose".
const vetting = [
  { icon: MagnifyingGlass, title: 'Written Application',   desc: 'Every recruiter starts with a written application and a portfolio of past placements.' },
  { icon: Handshake,       title: 'Interview & Reference', desc: 'Personal call with our leadership and reference checks with previous students & colleges.' },
  { icon: ShieldCheck,     title: 'Compliance Review',     desc: 'Regulatory, PTIB, immigration ethics — anyone with a red flag is declined, no exceptions.' },
  { icon: SealCheck,       title: 'Signed Agreement',      desc: 'A binding partnership agreement enforcing our code of conduct — annually renewed.' },
];

const exams = ['iTEP', 'Accuplacer', 'CLBPT / CLBP', 'IELTS', 'Other Entrance Tests'];

// Home page — repositioned around the "exclusive / by-invitation only" story.
// Copy avoids "we help everyone" language and leans on scarcity + rigor.
export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950/85 via-blue-950/75 to-blue-950/50" />
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
              <Sparkle size={14} weight="fill" className="text-amber-300" />
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/90">By Invitation Only</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              The colleges we<br />
              believe in.<br />
              <span className="text-blue-300">The recruiters we trust.</span>
            </h1>
            <p className="text-lg text-blue-100/85 leading-relaxed mb-8 max-w-xl">
              Study Alliance is a small, carefully chosen partnership network. Every college on our roster is one we&rsquo;ve visited in person, and every recruiter is welcomed in through a considered four-stage introduction.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/apply" className="inline-flex items-center gap-2 bg-white text-blue-900 px-6 py-3 rounded-md font-semibold hover:bg-blue-50 transition-all hover:-translate-y-0.5 shadow-lg shadow-black/20" data-testid="hero-apply-btn">
                Apply as a Student <ArrowRight size={18} />
              </Link>
              <Link to="/recruiter-signup" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-md font-semibold hover:bg-white/10 transition-all" data-testid="hero-recruiter-btn">
                Request Recruiter Invite
              </Link>
            </div>
            <p className="text-[11px] text-blue-200/70 mt-6 max-w-md">
              Recruiter applications are reviewed personally by our team. You&rsquo;ll hear back from us within 48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Scarcity stats */}
      <section className="bg-white border-b border-gray-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map(s => (
              <div key={s.label} className="text-center">
                <p className="text-3xl lg:text-4xl font-bold text-blue-700 tabular-nums" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.value}</p>
                <p className="text-gray-800 text-sm font-semibold mt-1">{s.label}</p>
                <p className="text-gray-500 text-xs mt-0.5">{s.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vetting funnel — the "why we\u2019re selective" story */}
      <section className="py-24 bg-slate-950 text-white relative overflow-hidden" data-testid="vetting-section">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-amber-300 font-bold text-[11px] uppercase tracking-[0.16em] mb-3">How We Welcome Recruiters</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              A four-stage introduction. Considered, not automated.
            </h2>
            <p className="text-blue-100/80 mt-4 text-base leading-relaxed">
              We prefer to work with a small circle of ethical, experienced recruiters. Every partnership we sign is one we can stand behind — with our students, our colleges, and the regulators who trust us.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {vetting.map((v, i) => (
              <div key={v.title} className="relative rounded-2xl p-6 bg-white/5 border border-white/10 hover:border-amber-300/40 hover:bg-white/[0.08] transition-all backdrop-blur">
                <div className="absolute top-4 right-4 text-4xl font-bold text-white/10 tabular-nums" style={{ fontFamily: "'Libre Baskerville', serif" }}>0{i + 1}</div>
                <div className="w-11 h-11 rounded-xl bg-amber-300/15 border border-amber-300/30 flex items-center justify-center mb-4">
                  <v.icon size={22} weight="fill" className="text-amber-300" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{v.title}</h3>
                <p className="text-blue-100/70 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-4 text-sm text-blue-100/70">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> 65% declined at stage 2
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> 18% declined at stage 3
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> 8% signed
            </span>
          </div>
        </div>
      </section>

      {/* College network — small on purpose. Names withheld by design. */}
      <section className="py-24 bg-white" data-testid="colleges-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-blue-700 font-bold text-[11px] uppercase tracking-[0.16em] mb-3">A Small, Deliberate Network</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            A short list of Canadian institutions. Personally verified.
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base leading-relaxed mb-12">
            We don&rsquo;t publish our partners publicly. Every college on our roster is one we&rsquo;ve visited, whose programs we&rsquo;ve stress-tested against provincial regulators, and whose registrars we&rsquo;re on a first-name basis with. Institution names are shared privately with approved recruiters and shortlisted students.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { region: 'Lower Mainland', province: 'British Columbia' },
              { region: 'Interior', province: 'British Columbia' },
              { region: 'Greater Toronto', province: 'Ontario' },
              { region: 'York Region', province: 'Ontario' },
            ].map((c, i) => (
              <div key={c.region + i} className="p-5 rounded-2xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all bg-gradient-to-b from-white to-blue-50/30">
                <div className="w-10 h-10 mx-auto rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
                  <GraduationCap size={20} weight="fill" />
                </div>
                <p className="text-sm font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Partner Institution</p>
                <p className="text-xs text-gray-500 mt-0.5">{c.region} · {c.province}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-8">Institution names are disclosed only after eligibility review — not before.</p>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-slate-50/70" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-blue-700 font-bold text-[11px] uppercase tracking-[0.16em] mb-3">What Selected Students Get</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              End-to-end support. Nothing outsourced.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map(s => (
              <div key={s.title} className="group p-6 bg-white border border-gray-200 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 ring-4 ring-blue-100/60 group-hover:bg-blue-100 transition-colors">
                  <s.icon size={22} weight="fill" className="text-blue-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exam Prep */}
      <section className="py-24 bg-white" data-testid="exam-prep-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="text-blue-700 font-bold text-[11px] uppercase tracking-[0.16em] mb-3">Exam Preparation</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-6" style={{ fontFamily: "'Libre Baskerville', serif" }}>Clear your exams with confidence.</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Focused preparation for the exams our partner colleges actually accept. Small cohorts, real tutors, no generic YouTube playlists.
              </p>
              <div className="space-y-3 mb-8">
                {exams.map(e => (
                  <div key={e} className="flex items-center gap-3">
                    <CheckCircle size={20} weight="fill" className="text-blue-500 shrink-0" />
                    <span className="text-gray-700 font-medium">{e}</span>
                  </div>
                ))}
              </div>
              <Link to="/exam-prep" className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-800 transition-all" data-testid="exam-learn-more-btn">
                Learn More <ArrowRight size={18} />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmd8ZW58MHx8fHwxNzc2MzY1NTY0fDA&ixlib=rb-4.1.0&q=85" alt="Exam preparation" className="rounded-2xl shadow-lg w-full object-cover max-h-[480px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Recruiter CTA — reframed as invite request */}
      <section className="py-24 bg-slate-50/70" data-testid="recruiter-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl p-8 sm:p-12 lg:p-16 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-center">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
            <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
                <Star size={12} weight="fill" className="text-amber-300" />
                <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/90">Recruiter Applications Open</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                Think you&rsquo;d be a great fit?
              </h2>
              <p className="text-blue-100/85 max-w-2xl mx-auto mb-8 text-base lg:text-lg leading-relaxed">
                We&rsquo;re small by design. If you place ethically, know your regulations, and treat students like people, we&rsquo;d genuinely love to hear from you.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/recruiter-signup" className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-all hover:-translate-y-0.5" data-testid="cta-recruiter-signup">
                  Request an Invite <ArrowRight size={18} />
                </Link>
                <Link to="/for-recruiters" className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-all" data-testid="cta-learn-more">
                  Read the Standards
                </Link>
              </div>
              <p className="text-[11px] text-blue-200/70 mt-6">Manual review · reply within 48 hours · references required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust — reframed */}
      <section className="py-24 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <p className="text-blue-700 font-bold text-[11px] uppercase tracking-[0.16em] mb-3">Why This Matters</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Small circle. Real accountability.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'One recruiter per student',      desc: 'No handoffs, no confusion. The recruiter who signs your student is the one who sees them enrolled.' },
              { title: 'Colleges we can call directly',  desc: 'Registrars know us by name. When a document is missing, it&rsquo;s a phone call, not a two-week email chain.' },
              { title: 'Compliance is non-negotiable',   desc: 'PTIB, IRCC guidance, provincial rules — we build to the strictest interpretation, not the loosest.' },
            ].map(t => (
              <div key={t.title} className="p-8 bg-white border border-gray-200 rounded-2xl text-left hover:shadow-md hover:border-blue-200 transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 flex items-center justify-center mb-4">
                  <ShieldCheck size={20} weight="fill" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>{t.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: t.desc }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
